import { getEvidenceDocumentRoot } from "./reportEvidencePointer";
import { formatDirectionBarsWindow } from "./marketRegimeDirectionLookbacks";

type PlainRecord = Record<string, unknown>;

function isPlainRecord(v: unknown): v is PlainRecord {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

export type ConditionAnalysisRegimeKey =
  | "uptrend"
  | "downtrend"
  | "range"
  | "choppy"
  | "unknown"
  /** Legacy v2 — still present on older report snapshots. */
  | "accumulation"
  | "expansion"
  | "trend"
  | "volatile"
  | string;

export type CandleDirectionKey = "up" | "down" | "flat" | "unknown" | string;

export type MarketContextCandleDirection = "up" | "down" | "flat";

/** Single-dimension pivot rows (regime, direction, session, market candle direction). */
export function isPivotEnvironmentSlice(slice: ConditionAnalysisSlice | null | undefined): boolean {
  if (!slice || slice.kind === "all") return false;
  return (
    slice.kind === "market_direction" ||
    slice.kind === "market_direction_short" ||
    slice.kind === "candle_direction" ||
    slice.kind === "candle_direction_short" ||
    slice.kind === "direction" ||
    slice.kind === "regime" ||
    slice.kind === "session_time"
  );
}

export type ConditionAnalysisSummaryRow = {
  key: string;
  win_rate?: number;
  total_pnl?: number;
  expectancy?: number;
  sample_size?: number;
};

export type ConditionAnalysisBucketRow = {
  bucket: string;
  win_rate?: number;
  total_pnl?: number;
  confidence?: number;
  expectancy?: number;
  sample_size?: number;
  /** Optional BE key matching `regime_breakdown.by_market_condition_and_direction_short_and_trade_direction`. */
  segment_key?: string;
  /** Alias for segment_key (some payloads use drill_key / deep_dive_key). */
  drill_key?: string;
  segment_context?: string;
  universe?: string;
  kind?: string;
  /** Display-only label from snapshot — never use for cohort resolve target. */
  label?: string;
  policy_import_nodes?: unknown[];
  /** Optional machine-readable simulator mapping from backend (filter_catalog.v7+). */
  simulator_suggestions?: unknown[];
};

export type ConditionAnalysisSituationClause = { key: string; value: string };

export type ConditionAnalysisSituationRow = {
  clauses?: ConditionAnalysisSituationClause[];
  situation?: string;
  win_rate?: number;
  total_pnl?: number;
  confidence?: number;
  expectancy?: number;
  sample_size?: number;
  quality_score?: number;
  /** Optional BE key for short-window triple segment drill-down. */
  segment_key?: string;
  drill_key?: string;
  segment_context?: string;
  universe?: string;
  kind?: string;
  /** Display-only label from snapshot — never use for cohort resolve target. */
  label?: string;
  policy_import_nodes?: unknown[];
  simulator_suggestions?: unknown[];
};

export type ConditionAnalysisMarketConditionBrackets = {
  best_situations?: ConditionAnalysisSituationRow[];
  worst_situations?: ConditionAnalysisSituationRow[];
  evaluated_situations?: number;
  definitions_note?: string;
  bucket_metric_definitions_ref?: string;
  bucket_metric_definitions?: Record<string, unknown>;
};

/** When a slice’s marginal dimension tables are all one row with identical metrics (no within-slice variation on those axes). */
export type ConditionAnalysisSegmentAggregate = {
  market_condition?: string;
  candle_direction?: string;
  trade_direction?: string;
  triple_key?: string;
  sample_size?: number;
  win_rate?: number;
  expectancy?: number;
  total_pnl?: number;
};

export type ConditionAnalysisUniformSegmentContext = {
  marginal_tables_redundant?: boolean;
  segment_aggregate?: ConditionAnalysisSegmentAggregate;
  ui_hint?: string;
};

/** Why a marginal table was omitted for the current slice (BE-driven UX). */
export type ConditionAnalysisTableOmittedEntry = {
  /** Canonical table id when provided (e.g. trade_direction). */
  table_key?: string;
  message: string;
};

/** Describes which cohort a condition-analysis node was computed on (full report vs nested slice). */
export type ConditionAnalysisScope = {
  /** Optional JSON-pointer–style path in the stats payload. */
  path?: string;
  /** Optional stable segment id (may match breakdown map keys). */
  segment_key?: string;
  /** Trades included in this node’s metrics. */
  trade_count?: number;
};

export type ConditionAnalysisNode = {
  version?: string;
  /** When present, clarifies whether metrics are full cohort vs a nested segment. */
  analysis_scope?: ConditionAnalysisScope;
  regime_summary?: {
    notes?: string[];
    market_condition?: ConditionAnalysisSummaryRow[];
    market_condition_and_direction?: ConditionAnalysisSummaryRow[];
    candle_direction?: ConditionAnalysisSummaryRow[];
    /** Short-window candle direction (TF-scaled lookback; fallback ~120 bars), keys: up/down/flat. */
    candle_direction_short?: ConditionAnalysisSummaryRow[];
    trade_direction?: ConditionAnalysisSummaryRow[];
    /** Marginal table by trading session at entry (keys align with `session_at_entry.session_id` / catalog). */
    session?: ConditionAnalysisSummaryRow[];
    candle_direction_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /** Keys like down|short, up|long. */
    candle_direction_short_and_trade_direction?: ConditionAnalysisSummaryRow[];
    market_condition_candle_direction_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /** Keys like trend|down|short. */
    market_condition_candle_direction_short_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{long|short}` — session × trade side at entry. */
    session_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{up|down|flat}` — session × long-window candle direction. */
    session_and_candle_direction?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{up|down|flat}` — session × short-window candle direction. */
    session_and_candle_direction_short?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{market_condition}` */
    session_and_market_condition?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{condition}|{long|short}` */
    session_and_market_condition_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{condition}|{candleDir}|{long|short}` long window. */
    session_and_market_condition_and_direction_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /** Keys `{session}|{condition}|{candleDirShort}|{long|short}` */
    session_and_market_condition_and_direction_short_and_trade_direction?: ConditionAnalysisSummaryRow[];
    /**
     * Worst short-window triple segments (regime × TF-scaled short direction × trade side).
     * Drill-down: `regime_breakdown.by_market_condition_and_direction_short_and_trade_direction[row.key]`.
     */
    worst_market_condition_x_direction_short_x_trade_detail?: ConditionAnalysisSummaryRow[];
    uniform_segment_context?: ConditionAnalysisUniformSegmentContext;
    /** Human-readable reasons tables were omitted for this slice (optional UX). */
    tables_omitted_for_slice_context?: ConditionAnalysisTableOmittedEntry[];
  };
  best_conditions?: ConditionAnalysisBucketRow[];
  worst_conditions?: ConditionAnalysisBucketRow[];
  evaluated_buckets?: number;
  evaluated_situations?: number;
  min_sample_size?: number;
  price_excursion_summary?: Record<string, unknown>;
  market_condition_brackets?: ConditionAnalysisMarketConditionBrackets;
  directional_condition_analysis?: { long?: ConditionAnalysisNode; short?: ConditionAnalysisNode };
  regime_breakdown?: {
    by_market_condition?: Record<string, ConditionAnalysisNode>;
    /** Long-window candle direction subsets (up / down / flat). */
    by_candle_direction?: Record<string, ConditionAnalysisNode>;
    /**
     * Recent TF-scaled `direction_short` (same deadband as BE); same nested shape as `by_candle_direction`.
     */
    by_candle_direction_short?: Record<string, ConditionAnalysisNode>;
    /** Up/down bars only (no flat); long window. */
    by_market_direction?: Record<string, ConditionAnalysisNode>;
    /** Up/down only from short-window candle direction (no flat). */
    by_market_direction_short?: Record<string, ConditionAnalysisNode>;
    /** Keys like <cond>|<dir_short>|<trade_dir> (short-window direction). */
    by_market_condition_and_direction_short_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    by_market_condition_and_direction_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    /** Keys like down|long — long-window candle direction × trade side. */
    by_candle_direction_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    /** Same as above using short-window (TF-scaled) candle direction. */
    by_candle_direction_short_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    /** Session-first cohorts: keys `{session_id}` (see backend regime_breakdown notes). */
    by_session?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{long|short}` */
    by_session_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{up|down|flat}` long-window candle direction at entry. */
    by_session_and_candle_direction?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{up|down|flat}` short-horizon direction. */
    by_session_and_candle_direction_short?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{market_condition}` */
    by_session_and_market_condition?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{condition}|{long|short}` */
    by_session_and_market_condition_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{condition}|{candleDir}|{long|short}` long-window candle dir. */
    by_session_and_market_condition_and_direction_and_trade_direction?: Record<string, ConditionAnalysisNode>;
    /** `{session}|{condition}|{candleDirShort}|{long|short}` */
    by_session_and_market_condition_and_direction_short_and_trade_direction?: Record<string, ConditionAnalysisNode>;
  };
  /** Optional notes from `regime_breakdown.notes` on the same JSON object as the maps. */
  regime_breakdown_notes?: string[];
};

export type ConditionAnalysisSliceKind =
  | "all"
  | "direction"
  | "candle_direction"
  | "candle_direction_short"
  | "market_direction"
  | "market_direction_short"
  | "regime"
  | "regime_direction"
  | "regime_direction_trade_direction"
  | "candle_direction_trade_direction"
  | "candle_direction_short_trade_direction"
  | "session_time"
  | "session_trade_direction"
  | "session_candle_direction"
  | "session_candle_direction_short"
  | "session_regime"
  | "session_regime_trade_direction"
  | "session_regime_direction_trade_direction"
  | "session_regime_direction_short_trade_direction";

export type ConditionAnalysisSlice = {
  id: string;
  label: string;
  kind: ConditionAnalysisSliceKind;
  direction?: "long" | "short";
  market_condition?: ConditionAnalysisRegimeKey;
  candle_direction?: CandleDirectionKey;
  /** Trading session id at entry (marginal session summary / segment picks). */
  session_id?: string;
  node: ConditionAnalysisNode;
};

function toNumber(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function toString(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function parseSummaryRows(v: unknown): ConditionAnalysisSummaryRow[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: ConditionAnalysisSummaryRow[] = [];
  for (const item of v) {
    if (!isPlainRecord(item)) continue;
    const key = toString(item.key);
    if (!key) continue;
    out.push({
      key,
      win_rate: toNumber(item.win_rate),
      total_pnl: toNumber(item.total_pnl),
      expectancy: toNumber(item.expectancy),
      sample_size: toNumber(item.sample_size),
    });
  }
  return out.length ? out : undefined;
}

/** Maps `stats.session_analysis.sessions[]` into regime-summary marginal row shape (when BE omits `regime_summary.session`). */
function sessionAnalysisSessionsToSummaryRows(sessions: unknown[]): ConditionAnalysisSummaryRow[] | undefined {
  const out: ConditionAnalysisSummaryRow[] = [];
  for (const item of sessions) {
    if (!isPlainRecord(item)) continue;
    const key =
      toString(item.session_id) ??
      toString(item.sessionId) ??
      toString(item.id) ??
      toString(item.name);
    if (!key) continue;
    const tc = toNumber(item.trade_count);
    const wt = toNumber(item.winning_trades);
    const lt = toNumber(item.losing_trades);
    const scratch = toNumber(item.scratch_trades);
    const sampleFromParts =
      wt != null && lt != null ? wt + lt + (scratch != null && Number.isFinite(scratch) ? scratch : 0) : undefined;
    const sample = tc ?? sampleFromParts;
    out.push({
      key,
      win_rate: toNumber(item.win_rate),
      total_pnl: toNumber(item.total_pnl),
      expectancy: toNumber(item.average_pnl ?? item.expectancy),
      sample_size: sample,
    });
  }
  return out.length ? out : undefined;
}

/** Resolve `sessions[]` from common stats / bundle shapes (snake_case + camelCase, nested under aggregated). */
function findSessionAnalysisSessionsArrays(stats: PlainRecord): unknown[] | null {
  const sessionsFromBlock = (block: unknown): unknown[] | null => {
    if (!isPlainRecord(block)) return null;
    const raw =
      block.sessions ??
      (block as PlainRecord).session_list ??
      (block as PlainRecord).items;
    return Array.isArray(raw) && raw.length ? raw : null;
  };

  const fromStats =
    sessionsFromBlock(stats.session_analysis) ??
    sessionsFromBlock((stats as PlainRecord).sessionAnalysis);
  if (fromStats) return fromStats;

  const agg = stats.aggregated;
  if (isPlainRecord(agg)) {
    const nested =
      sessionsFromBlock(agg.session_analysis) ??
      sessionsFromBlock((agg as PlainRecord).sessionAnalysis);
    if (nested) return nested;
  }

  return null;
}

/** Marginal bucket only, e.g. `session:london_new_york` (not compound `session:x|atr:y`). */
function sessionTokenFromMarginalBucket(bucket: string): string | null {
  const t = String(bucket || "").trim();
  const m = /^session:([^|]+)$/i.exec(t);
  const raw = m?.[1]?.trim();
  return raw || null;
}

/** Walk aggregate + every nested `regime_breakdown` + directional nodes to find all `session:*` marginal buckets. */
function collectAllBucketRowsDeep(node: ConditionAnalysisNode | undefined, out: ConditionAnalysisBucketRow[]): void {
  if (!node) return;
  if (node.best_conditions) {
    for (const r of node.best_conditions) out.push(r);
  }
  if (node.worst_conditions) {
    for (const r of node.worst_conditions) out.push(r);
  }

  const rb = node.regime_breakdown;
  if (rb) {
    const maps: Array<Record<string, ConditionAnalysisNode> | undefined> = [
      rb.by_market_condition,
      rb.by_candle_direction,
      rb.by_candle_direction_short,
      rb.by_market_direction,
      rb.by_market_direction_short,
      rb.by_market_condition_and_direction_short_and_trade_direction,
      rb.by_market_condition_and_direction_and_trade_direction,
      rb.by_candle_direction_and_trade_direction,
      rb.by_candle_direction_short_and_trade_direction,
      rb.by_session,
      rb.by_session_and_trade_direction,
      rb.by_session_and_candle_direction,
      rb.by_session_and_candle_direction_short,
      rb.by_session_and_market_condition,
      rb.by_session_and_market_condition_and_trade_direction,
      rb.by_session_and_market_condition_and_direction_and_trade_direction,
      rb.by_session_and_market_condition_and_direction_short_and_trade_direction,
    ];
    for (const m of maps) {
      if (!m) continue;
      for (const child of Object.values(m)) {
        collectAllBucketRowsDeep(child, out);
      }
    }
  }

  const dca = node.directional_condition_analysis;
  if (dca?.long) collectAllBucketRowsDeep(dca.long, out);
  if (dca?.short) collectAllBucketRowsDeep(dca.short, out);
}

/**
 * Derive session marginal rows from every `session:<session_id>` bucket anywhere under condition analysis
 * (root, directional long/short, and all `regime_breakdown` drill nodes).
 */
function sessionRowsFromMarginalBuckets(node: ConditionAnalysisNode): ConditionAnalysisSummaryRow[] | undefined {
  const flat: ConditionAnalysisBucketRow[] = [];
  collectAllBucketRowsDeep(node, flat);

  const byLower = new Map<string, ConditionAnalysisSummaryRow>();
  for (const row of flat) {
    const token = sessionTokenFromMarginalBucket(row.bucket ?? "");
    if (!token) continue;
    const lk = token.toLowerCase();
    const sample = row.sample_size ?? Number.NEGATIVE_INFINITY;
    const prev = byLower.get(lk);
    const prevSample = prev?.sample_size ?? Number.NEGATIVE_INFINITY;
    if (!prev || sample > prevSample) {
      byLower.set(lk, {
        key: token,
        win_rate: row.win_rate,
        total_pnl: row.total_pnl,
        expectancy: row.expectancy,
        sample_size: row.sample_size,
      });
    }
  }
  if (!byLower.size) return undefined;
  const out: ConditionAnalysisSummaryRow[] = [];
  byLower.forEach((row) => {
    out.push(row);
  });
  return out.sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0));
}

/** Same as `sessionRowsFromMarginalBuckets` — kept so older call sites / merges still type-check. */
const sessionRowsFromMarginalBucketsDeep = sessionRowsFromMarginalBuckets;

function isPlaceholderUnknownSessionId(id: string): boolean {
  const x = id.trim().toLowerCase();
  return x === "unknown" || x === "none" || x === "";
}

function readTradeSessionId(t: PlainRecord): string | undefined {
  const fromBlock = (o: unknown): string | undefined => {
    if (!isPlainRecord(o)) return undefined;
    const id = toString(o.session_id ?? o.sessionId);
    const tid = id?.trim();
    if (!tid || isPlaceholderUnknownSessionId(tid)) return undefined;
    return tid;
  };

  const sae = t.session_at_entry ?? t.sessionAtEntry;
  if (isPlainRecord(sae)) {
    const canon = toString(sae.session_id ?? sae.sessionId)?.trim();
    if (canon && !isPlaceholderUnknownSessionId(canon)) return canon;
  }

  const st = t.session_time ?? t.sessionTime ?? t.entry_session_time ?? t.entrySessionTime;
  const a = fromBlock(st);
  if (a) return a;

  const mc = t.market_context ?? t.marketContext ?? t.market_context_at_entry ?? t.marketContextAtEntry;
  if (isPlainRecord(mc)) {
    const b = fromBlock(mc.session_time ?? mc.sessionTime);
    if (b) return b;
  }

  const ctx = t.context ?? t.entry_context ?? t.entryContext;
  if (isPlainRecord(ctx)) {
    const c = fromBlock(ctx.session_time ?? ctx.sessionTime);
    if (c) return c;
  }

  const at = t.at_entry ?? t.atEntry;
  if (isPlainRecord(at)) {
    const d = fromBlock(at.session_time ?? at.sessionTime);
    if (d) return d;
  }

  const top = toString(t.session_id ?? t.sessionId)?.trim();
  if (top && !isPlaceholderUnknownSessionId(top)) return top;
  return undefined;
}

/**
 * Aggregate session marginals from embedded `trades` (closed trades with `session_at_entry.session_id`,
 * `session_time.session_id`, or equivalent). Preferred over bucket mining when the bundle includes full
 * trade rows with session.
 */
function sessionRowsFromSnapshotTrades(doc: PlainRecord): ConditionAnalysisSummaryRow[] | undefined {
  const trades = doc.trades;
  if (!Array.isArray(trades) || trades.length === 0) return undefined;

  type Agg = { key: string; wins: number; losses: number; pnl: number; n: number };
  const byLower = new Map<string, Agg>();

  for (const raw of trades) {
    if (!isPlainRecord(raw)) continue;

    const status = String(raw.status ?? raw.trade_status ?? "").toLowerCase();
    if (status === "open") continue;

    const sid = readTradeSessionId(raw);
    const trimmed = sid?.trim();
    if (!trimmed) continue;

    const lk = trimmed.toLowerCase();

    const pnl = toNumber(
      raw.net_pnl ?? raw.netPnL ?? raw.net_pnl_usd ?? raw.realized_pnl ?? raw.realizedPnl ?? raw.gross_pnl ?? raw.grossPnL
    );

    let agg = byLower.get(lk);
    if (!agg) {
      agg = { key: trimmed, wins: 0, losses: 0, pnl: 0, n: 0 };
      byLower.set(lk, agg);
    }
    agg.n += 1;
    if (pnl != null && Number.isFinite(pnl)) agg.pnl += pnl;

    if (raw.is_win === true) agg.wins += 1;
    else if (raw.is_loss === true) agg.losses += 1;
    else if (pnl != null && Number.isFinite(pnl)) {
      if (pnl > 0) agg.wins += 1;
      else if (pnl < 0) agg.losses += 1;
    }
  }

  if (!byLower.size) return undefined;

  const out: ConditionAnalysisSummaryRow[] = [];
  byLower.forEach((agg) => {
    const denom = agg.wins + agg.losses;
    const win_rate = denom > 0 ? (agg.wins / denom) * 100 : undefined;
    out.push({
      key: agg.key,
      sample_size: agg.n,
      win_rate,
      total_pnl: agg.pnl,
      expectancy: agg.n > 0 ? agg.pnl / agg.n : undefined,
    });
  });
  return out.sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0));
}

function countClosedTradesInBundle(doc: PlainRecord): number {
  const trades = doc.trades;
  if (!Array.isArray(trades)) return 0;
  let n = 0;
  for (const raw of trades) {
    if (!isPlainRecord(raw)) continue;
    const status = String(raw.status ?? raw.trade_status ?? "").toLowerCase();
    if (status === "open") continue;
    n += 1;
  }
  return n;
}

/**
 * True when trade-derived session counts partition every closed trade in the bundle (same cohort as rows).
 * Then we prefer that table over `session_analysis` / buckets so a stale `unknown` row cannot inflate unknown.
 */
function tradeSessionMarginalsCoverAllClosedTrades(
  doc: PlainRecord,
  fromTrades: ConditionAnalysisSummaryRow[] | undefined
): boolean {
  if (!fromTrades?.length) return false;
  const closed = countClosedTradesInBundle(doc);
  if (closed <= 0) return false;
  const sum = fromTrades.reduce((s, r) => s + (r.sample_size ?? 0), 0);
  return sum === closed;
}

function preferTradeDerivedRegimeSummarySession(node: ConditionAnalysisNode, bundle: PlainRecord | null): ConditionAnalysisNode {
  if (!bundle) return node;
  const fromTrades = sessionRowsFromSnapshotTrades(bundle);
  if (!tradeSessionMarginalsCoverAllClosedTrades(bundle, fromTrades) || !fromTrades?.length) return node;
  return {
    ...node,
    regime_summary: {
      ...(node.regime_summary ?? {}),
      session: fromTrades,
    },
  };
}

/**
 * Union of every session id seen in trades, `session_analysis`, or marginal `session:*` buckets.
 *
 * For each session we pick **one** metrics row: whichever source has the **largest `sample_size`**
 * (so trade-level aggregation cannot “win” with 1 tagged trade when `session_analysis` has the full
 * cohort). Ties favor `session_analysis` over trades over buckets.
 */
function pickSessionRowMetricsForUnion(
  tRow: ConditionAnalysisSummaryRow | undefined,
  aRow: ConditionAnalysisSummaryRow | undefined,
  bRow: ConditionAnalysisSummaryRow | undefined
): ConditionAnalysisSummaryRow | undefined {
  type Tagged = { r: ConditionAnalysisSummaryRow; tier: number };
  const tagged: Tagged[] = [];
  if (aRow) tagged.push({ r: aRow, tier: 3 });
  if (tRow) tagged.push({ r: tRow, tier: 2 });
  if (bRow) tagged.push({ r: bRow, tier: 1 });
  if (!tagged.length) return undefined;

  tagged.sort((u, v) => {
    const su = u.r.sample_size ?? -1;
    const sv = v.r.sample_size ?? -1;
    if (sv !== su) return sv - su;
    return v.tier - u.tier;
  });

  const best = tagged[0].r;
  const key = best.key?.trim();
  if (!key) return undefined;
  return {
    key,
    sample_size: best.sample_size,
    win_rate: best.win_rate,
    total_pnl: best.total_pnl,
    expectancy: best.expectancy,
  };
}

function mergeSessionRowsUnionPreferTradeMetrics(
  fromTrades: ConditionAnalysisSummaryRow[] | undefined,
  fromAnalysis: ConditionAnalysisSummaryRow[] | undefined,
  fromBuckets: ConditionAnalysisSummaryRow[] | undefined
): ConditionAnalysisSummaryRow[] {
  const allLower = new Set<string>();
  for (const rows of [fromTrades, fromAnalysis, fromBuckets]) {
    for (const r of rows ?? []) {
      const k = r.key?.trim();
      if (k) allLower.add(k.toLowerCase());
    }
  }

  const findRow = (rows: ConditionAnalysisSummaryRow[] | undefined, lk: string): ConditionAnalysisSummaryRow | undefined => {
    for (const x of rows ?? []) {
      if (x.key?.trim().toLowerCase() === lk) return x;
    }
    return undefined;
  };

  const out: ConditionAnalysisSummaryRow[] = [];
  allLower.forEach((lk) => {
    const row = pickSessionRowMetricsForUnion(findRow(fromTrades, lk), findRow(fromAnalysis, lk), findRow(fromBuckets, lk));
    if (row) out.push(row);
  });

  return out.sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0));
}

function augmentRootRegimeSummarySessionFromStats(
  node: ConditionAnalysisNode,
  doc: PlainRecord,
  stats: PlainRecord
): ConditionAnalysisNode {
  if ((node.regime_summary?.session?.length ?? 0) > 0) return node;

  const rawSessions = findSessionAnalysisSessionsArrays(stats) ?? findSessionAnalysisSessionsArrays(doc);
  const fromAnalysis =
    rawSessions && rawSessions.length ? sessionAnalysisSessionsToSummaryRows(rawSessions) : undefined;
  const fromTrades = sessionRowsFromSnapshotTrades(doc);
  const fromBuckets = sessionRowsFromMarginalBucketsDeep(node);

  const merged = mergeSessionRowsUnionPreferTradeMetrics(fromTrades, fromAnalysis, fromBuckets);
  if (!merged.length) return node;

  return {
    ...node,
    regime_summary: {
      ...(node.regime_summary ?? {}),
      session: merged,
    },
  };
}

function parseOptionalSegmentKey(item: PlainRecord): { segment_key?: string; drill_key?: string } {
  const sk =
    toString(item.segment_key) ??
    toString(item.segmentKey) ??
    toString(item.drill_key) ??
    toString(item.drillKey) ??
    toString(item.deep_dive_key) ??
    toString(item.deepDiveKey);
  const t = sk?.trim();
  if (!t) return {};
  return { segment_key: t, drill_key: t };
}

function parseSimulatorSuggestions(item: PlainRecord): unknown[] | undefined {
  const raw =
    item.policy_import_nodes ??
    item.simulator_suggestions ??
    item.simulator_suggestion_nodes ??
    item.simulator_suggestion_node_for_metric_bucket ??
    item.simulator_suggestion_node_for_situation ??
    item.simulator_suggestion_node ??
    undefined;
  if (Array.isArray(raw)) return raw.length ? raw : undefined;
  if (raw && typeof raw === "object") return [raw];
  return undefined;
}

function parseBucketRows(v: unknown): ConditionAnalysisBucketRow[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: ConditionAnalysisBucketRow[] = [];
  for (const item of v) {
    if (!isPlainRecord(item)) continue;
    const bucket = toString(item.bucket);
    if (!bucket) continue;
    const suggestions = parseSimulatorSuggestions(item);
    const seg = parseOptionalSegmentKey(item);
    const segmentContext = toString(item.segment_context);
    const universe = toString(item.universe);
    const kind = toString(item.kind);
    const label = toString(item.label);
    out.push({
      bucket,
      win_rate: toNumber(item.win_rate),
      total_pnl: toNumber(item.total_pnl),
      confidence: toNumber(item.confidence),
      expectancy: toNumber(item.expectancy),
      sample_size: toNumber(item.sample_size),
      ...(segmentContext ? { segment_context: segmentContext } : {}),
      ...(universe ? { universe } : {}),
      ...(kind ? { kind } : {}),
      ...(label ? { label } : {}),
      ...(suggestions?.length ? { policy_import_nodes: suggestions, simulator_suggestions: suggestions } : {}),
      ...(seg.segment_key ? seg : {}),
    });
  }
  return out.length ? out : undefined;
}

function situationClauseScalarToString(v: unknown): string | undefined {
  if (typeof v === "string") {
    const t = v.trim();
    return t || undefined;
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  return undefined;
}

function parseSituationClauses(v: unknown): ConditionAnalysisSituationClause[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: ConditionAnalysisSituationClause[] = [];
  for (const item of v) {
    if (!isPlainRecord(item)) continue;
    const keyRaw =
      toString(item.key) ??
      situationClauseScalarToString(item.metric) ??
      situationClauseScalarToString(item.dimension) ??
      situationClauseScalarToString(item.name) ??
      situationClauseScalarToString(item.field);
    const key = keyRaw?.trim();
    const value =
      situationClauseScalarToString(item.value) ??
      situationClauseScalarToString(item.bucket) ??
      situationClauseScalarToString(item.val) ??
      situationClauseScalarToString(item.v);
    if (!key || !value) continue;
    out.push({ key, value });
  }
  return out.length ? out : undefined;
}

function parseSituations(v: unknown): ConditionAnalysisSituationRow[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: ConditionAnalysisSituationRow[] = [];
  for (const item of v) {
    if (!isPlainRecord(item)) continue;
    const seg = parseOptionalSegmentKey(item);
    const suggestions = parseSimulatorSuggestions(item);
    const segmentContext = toString(item.segment_context);
    const universe = toString(item.universe);
    const kind = toString(item.kind);
    const label = toString(item.label);
    out.push({
      clauses: parseSituationClauses(item.clauses),
      situation: toString(item.situation),
      win_rate: toNumber(item.win_rate),
      total_pnl: toNumber(item.total_pnl),
      confidence: toNumber(item.confidence),
      expectancy: toNumber(item.expectancy),
      sample_size: toNumber(item.sample_size),
      quality_score: toNumber(item.quality_score),
      ...(segmentContext ? { segment_context: segmentContext } : {}),
      ...(universe ? { universe } : {}),
      ...(kind ? { kind } : {}),
      ...(label ? { label } : {}),
      ...(suggestions?.length ? { policy_import_nodes: suggestions, simulator_suggestions: suggestions } : {}),
      ...(seg.segment_key ? seg : {}),
    });
  }
  return out.length ? out : undefined;
}

function parseSegmentAggregate(v: unknown): ConditionAnalysisSegmentAggregate | undefined {
  if (!isPlainRecord(v)) return undefined;
  const out: ConditionAnalysisSegmentAggregate = {
    market_condition: toString(v.market_condition),
    candle_direction: toString(v.candle_direction),
    trade_direction: toString(v.trade_direction),
    triple_key: toString(v.triple_key),
    sample_size: toNumber(v.sample_size),
    win_rate: toNumber(v.win_rate),
    expectancy: toNumber(v.expectancy),
    total_pnl: toNumber(v.total_pnl),
  };
  const hasAny =
    out.market_condition ||
    out.candle_direction ||
    out.trade_direction ||
    out.triple_key ||
    out.sample_size != null ||
    out.win_rate != null ||
    out.expectancy != null ||
    out.total_pnl != null;
  return hasAny ? out : undefined;
}

function parseUniformSegmentContext(v: unknown): ConditionAnalysisUniformSegmentContext | undefined {
  if (!isPlainRecord(v)) return undefined;
  const marginal_tables_redundant = v.marginal_tables_redundant === true ? true : undefined;
  const ui_hint = toString(v.ui_hint);
  const segment_aggregate = parseSegmentAggregate(v.segment_aggregate);
  if (!marginal_tables_redundant && !ui_hint && !segment_aggregate) return undefined;
  return {
    marginal_tables_redundant,
    segment_aggregate,
    ui_hint,
  };
}

function cohortSampleSizeFromNode(node: ConditionAnalysisNode): number | undefined {
  const tc = node.analysis_scope?.trade_count;
  const tcFloor = typeof tc === "number" && Number.isFinite(tc) ? Math.floor(tc) : undefined;

  const tdRowsEarly = node.regime_summary?.trade_direction;
  let tdSumEarly = 0;
  if (tdRowsEarly?.length) {
    for (const r of tdRowsEarly) {
      const n = r.sample_size;
      if (typeof n === "number" && Number.isFinite(n)) tdSumEarly += Math.floor(n);
    }
  }
  // Multiple trade-direction buckets (e.g. long + short) partition the cohort. When their sum disagrees with
  // `analysis_scope.trade_count`, prefer the marginal sum — payloads sometimes ship a stale or narrower scope count.
  if (tdRowsEarly && tdRowsEarly.length >= 2 && tdSumEarly > 0) {
    if (tcFloor == null || tdSumEarly !== tcFloor) return tdSumEarly;
    return tcFloor;
  }

  if (tcFloor != null && tcFloor > 0) return tcFloor;

  const segN = node.regime_summary?.uniform_segment_context?.segment_aggregate?.sample_size;
  if (typeof segN === "number" && Number.isFinite(segN) && segN > 0) return Math.floor(segN);

  const mc = node.regime_summary?.market_condition;
  if (mc?.length) {
    let s = 0;
    for (const r of mc) {
      const n = r.sample_size;
      if (typeof n === "number" && Number.isFinite(n)) s += n;
    }
    if (s > 0) return s;
  }

  const sumTable = (rows: ConditionAnalysisSummaryRow[] | undefined): number | undefined => {
    if (!rows?.length) return undefined;
    let s = 0;
    for (const r of rows) {
      const n = r.sample_size;
      if (typeof n === "number" && Number.isFinite(n)) s += n;
    }
    return s > 0 ? s : undefined;
  };
  const fromSession = sumTable(node.regime_summary?.session);
  if (fromSession != null) return fromSession;
  const fromTd = sumTable(node.regime_summary?.trade_direction);
  if (fromTd != null) return fromTd;

  const bc0 = node.best_conditions?.[0];
  const bs = bc0?.sample_size;
  if (typeof bs === "number" && Number.isFinite(bs) && bs > 0) return Math.floor(bs);

  return undefined;
}

function cohortMetricsFromNestedNode(node: ConditionAnalysisNode): Pick<
  ConditionAnalysisSummaryRow,
  "win_rate" | "total_pnl" | "expectancy"
> {
  const seg = node.regime_summary?.uniform_segment_context?.segment_aggregate;
  if (seg && (seg.win_rate != null || seg.total_pnl != null || seg.expectancy != null)) {
    return { win_rate: seg.win_rate, total_pnl: seg.total_pnl, expectancy: seg.expectancy };
  }

  const tc = node.analysis_scope?.trade_count;
  const matchesScope = (n: number | undefined): boolean =>
    typeof tc === "number" &&
    tc > 0 &&
    typeof n === "number" &&
    Number.isFinite(n) &&
    Math.floor(n) === Math.floor(tc);

  /** Whole-segment headline: one marginal row spanning the same trade count as `analysis_scope`. */
  const pickSpanningMarginal = (
    rows: ConditionAnalysisSummaryRow[] | undefined
  ): ConditionAnalysisSummaryRow | undefined => {
    if (!rows?.length) return undefined;
    if (rows.length === 1 && matchesScope(rows[0].sample_size)) return rows[0];
    return undefined;
  };

  /**
   * Several marginal rows (e.g. long + short) that partition the segment — when their sample_sizes sum to
   * `analysis_scope.trade_count`, derive headline metrics without mixing in unrelated tables.
   * For `trade_direction` only, still aggregate when the sum disagrees with scope (same mismatch as sample size).
   */
  const aggregateMarginalRowsIfScopeComplete = (
    rows: ConditionAnalysisSummaryRow[] | undefined,
    opts?: { tradeDirectionPartition?: boolean }
  ): ConditionAnalysisSummaryRow | undefined => {
    if (!rows || rows.length < 2) return undefined;
    let sum = 0;
    for (const r of rows) sum += r.sample_size ?? 0;
    const allowTradeDirMismatch =
      opts?.tradeDirectionPartition === true && sum > 0 && !matchesScope(sum);
    if (!matchesScope(sum) && !allowTradeDirMismatch) return undefined;
    let totalPnl = 0;
    let wrWeighted = 0;
    let exWeighted = 0;
    for (const r of rows) {
      const n = r.sample_size ?? 0;
      if (typeof r.total_pnl === "number" && Number.isFinite(r.total_pnl)) totalPnl += r.total_pnl;
      if (typeof r.win_rate === "number" && Number.isFinite(r.win_rate) && n > 0) wrWeighted += r.win_rate * n;
      if (typeof r.expectancy === "number" && Number.isFinite(r.expectancy) && n > 0) exWeighted += r.expectancy * n;
    }
    return {
      key: "__segment_aggregate__",
      sample_size: sum,
      win_rate: sum > 0 ? wrWeighted / sum : undefined,
      total_pnl: totalPnl,
      expectancy: sum > 0 ? exWeighted / sum : undefined,
    };
  };

  const tdRows = node.regime_summary?.trade_direction;
  const tdSpan =
    pickSpanningMarginal(tdRows) ??
    aggregateMarginalRowsIfScopeComplete(tdRows, { tradeDirectionPartition: true });
  if (tdSpan) {
    return { win_rate: tdSpan.win_rate, total_pnl: tdSpan.total_pnl, expectancy: tdSpan.expectancy };
  }
  const cdRows = node.regime_summary?.candle_direction;
  const cdSpan = pickSpanningMarginal(cdRows) ?? aggregateMarginalRowsIfScopeComplete(cdRows);
  if (cdSpan) {
    return { win_rate: cdSpan.win_rate, total_pnl: cdSpan.total_pnl, expectancy: cdSpan.expectancy };
  }
  const cdsRows = node.regime_summary?.candle_direction_short;
  const cdsSpan = pickSpanningMarginal(cdsRows) ?? aggregateMarginalRowsIfScopeComplete(cdsRows);
  if (cdsSpan) {
    return { win_rate: cdsSpan.win_rate, total_pnl: cdsSpan.total_pnl, expectancy: cdsSpan.expectancy };
  }

  const mcRows = node.regime_summary?.market_condition;
  const mcAggSpan = aggregateMarginalRowsIfScopeComplete(mcRows);
  if (mcAggSpan && (mcRows?.length ?? 0) > 1) {
    return { win_rate: mcAggSpan.win_rate, total_pnl: mcAggSpan.total_pnl, expectancy: mcAggSpan.expectancy };
  }
  if (mcRows?.length === 1) {
    const r = mcRows[0];
    return { win_rate: r.win_rate, total_pnl: r.total_pnl, expectancy: r.expectancy };
  }
  const bc = node.best_conditions?.[0];
  if (bc) {
    return {
      win_rate: bc.win_rate ?? undefined,
      total_pnl: bc.total_pnl ?? undefined,
      expectancy: bc.expectancy ?? undefined,
    };
  }
  return {};
}

/**
 * Sample + headline metrics for a nested condition-analysis node (e.g. `regime_breakdown.by_session.*`) when there is
 * no matching row in a flat marginal table — used by the Market Context pivot and similar summaries.
 */
export function cohortHeadlineFromNode(node: ConditionAnalysisNode): {
  sample: number | undefined;
  winRate: number | undefined;
  totalPnl: number | undefined;
  expectancy: number | undefined;
} {
  const sample = cohortSampleSizeFromNode(node);
  const m = cohortMetricsFromNestedNode(node);
  return {
    sample,
    winRate: m.win_rate,
    totalPnl: m.total_pnl,
    expectancy: m.expectancy,
  };
}

/**
 * Build `regime_summary`-shaped marginal rows from `regime_breakdown.by_session_and_*` nested nodes when the BE
 * omits pre-flattened `regime_summary.session_and_*` arrays.
 */
export function summaryRowsFromRegimeBreakdownMap(
  map: Record<string, ConditionAnalysisNode> | undefined
): ConditionAnalysisSummaryRow[] | undefined {
  if (!map) return undefined;
  const entries = Object.entries(map);
  if (!entries.length) return undefined;
  const out: ConditionAnalysisSummaryRow[] = [];
  for (const [key, child] of entries) {
    if (!key.trim() || !child) continue;
    const sample_size = cohortSampleSizeFromNode(child);
    const m = cohortMetricsFromNestedNode(child);
    out.push({
      key,
      sample_size: sample_size ?? undefined,
      ...m,
    });
  }
  if (!out.length) return undefined;
  return out.sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0));
}

export function mergedSessionMarginalRows(
  explicit: ConditionAnalysisSummaryRow[] | undefined | null,
  map: Record<string, ConditionAnalysisNode> | undefined
): ConditionAnalysisSummaryRow[] | undefined {
  if (explicit && explicit.length > 0) return explicit;
  return summaryRowsFromRegimeBreakdownMap(map);
}

function parseTablesOmittedForSliceContext(v: unknown): ConditionAnalysisTableOmittedEntry[] | undefined {
  if (v == null) return undefined;
  if (Array.isArray(v)) {
    if (v.length === 0) return undefined;
    const out: ConditionAnalysisTableOmittedEntry[] = [];
    for (const item of v) {
      if (typeof item === "string") {
        const t = item.trim();
        if (t) out.push({ message: t });
        continue;
      }
      if (isPlainRecord(item)) {
        const table_key = toString(item.table_key ?? item.table ?? item.key ?? item.name);
        const message =
          toString(item.message ?? item.reason ?? item.hint ?? item.text ?? item.explanation) ??
          (table_key ? `Omitted: ${table_key}` : undefined);
        const m = message?.trim();
        if (m) out.push({ ...(table_key ? { table_key } : {}), message: m });
      }
    }
    return out.length ? out : undefined;
  }
  if (isPlainRecord(v)) {
    const out: ConditionAnalysisTableOmittedEntry[] = [];
    for (const [k, val] of Object.entries(v)) {
      if (typeof val === "string") {
        const m = val.trim();
        if (m) out.push({ table_key: k, message: m });
        continue;
      }
      if (isPlainRecord(val)) {
        const message =
          toString(val.message) ?? toString(val.reason) ?? toString(val.hint) ?? JSON.stringify(val);
        const m = message?.trim();
        if (m) out.push({ table_key: k, message: m });
      }
    }
    return out.length ? out : undefined;
  }
  return undefined;
}

function parseAnalysisScope(v: unknown): ConditionAnalysisScope | undefined {
  if (!isPlainRecord(v)) return undefined;
  const path = toString(v.path ?? v.json_path ?? v.pointer ?? v.jsonPointer);
  const segment_key = toString(v.segment_key ?? v.segmentKey);
  const trade_count = toNumber(v.trade_count ?? v.tradeCount ?? v.n);
  if (!path && !segment_key && trade_count == null) return undefined;
  return {
    ...(path ? { path } : {}),
    ...(segment_key ? { segment_key } : {}),
    ...(trade_count != null ? { trade_count } : {}),
  };
}

function parseMarketConditionBrackets(v: unknown): ConditionAnalysisMarketConditionBrackets | undefined {
  if (!isPlainRecord(v)) return undefined;
  const best = parseSituations(v.best_situations);
  const worst = parseSituations(v.worst_situations);
  const bmd = isPlainRecord(v.bucket_metric_definitions) ? (v.bucket_metric_definitions as Record<string, unknown>) : undefined;
  if (!best && !worst && !bmd && !toString(v.bucket_metric_definitions_ref)) return undefined;
  return {
    best_situations: best,
    worst_situations: worst,
    evaluated_situations: toNumber(v.evaluated_situations),
    definitions_note: toString(v.definitions_note),
    bucket_metric_definitions_ref: toString(v.bucket_metric_definitions_ref),
    bucket_metric_definitions: bmd,
  };
}

/** Parse string-keyed nested condition_analysis objects under `regime_breakdown.*`. */
function parseRegimeBreakdownBranchMap(raw: unknown): Record<string, ConditionAnalysisNode> {
  const out: Record<string, ConditionAnalysisNode> = {};
  if (!isPlainRecord(raw)) return out;
  for (const [k, v] of Object.entries(raw)) {
    const n = parseNode(v);
    if (n) out[k] = n;
  }
  return out;
}

function parseNode(raw: unknown): ConditionAnalysisNode | null {
  if (!isPlainRecord(raw)) return null;

  const regime_summary_raw = isPlainRecord(raw.regime_summary) ? raw.regime_summary : null;
  const dca_raw = isPlainRecord(raw.directional_condition_analysis) ? raw.directional_condition_analysis : null;
  const regime_breakdown_raw = isPlainRecord(raw.regime_breakdown) ? raw.regime_breakdown : null;
  const by_mc_raw =
    regime_breakdown_raw && isPlainRecord(regime_breakdown_raw.by_market_condition)
      ? (regime_breakdown_raw.by_market_condition as Record<string, unknown>)
      : null;
  const by_mcdtd_raw =
    regime_breakdown_raw &&
    isPlainRecord(regime_breakdown_raw.by_market_condition_and_direction_and_trade_direction)
      ? (regime_breakdown_raw.by_market_condition_and_direction_and_trade_direction as Record<string, unknown>)
      : null;
  const by_mdir_raw =
    regime_breakdown_raw && isPlainRecord(regime_breakdown_raw.by_market_direction)
      ? (regime_breakdown_raw.by_market_direction as Record<string, unknown>)
      : null;
  const by_cd_raw =
    regime_breakdown_raw && isPlainRecord(regime_breakdown_raw.by_candle_direction)
      ? (regime_breakdown_raw.by_candle_direction as Record<string, unknown>)
      : null;
  const by_cd_short_raw =
    regime_breakdown_raw && isPlainRecord(regime_breakdown_raw.by_candle_direction_short)
      ? (regime_breakdown_raw.by_candle_direction_short as Record<string, unknown>)
      : null;
  const by_mdir_short_raw =
    regime_breakdown_raw && isPlainRecord(regime_breakdown_raw.by_market_direction_short)
      ? (regime_breakdown_raw.by_market_direction_short as Record<string, unknown>)
      : null;
  const by_mcd_short_td_raw =
    regime_breakdown_raw &&
    isPlainRecord(regime_breakdown_raw.by_market_condition_and_direction_short_and_trade_direction)
      ? (regime_breakdown_raw.by_market_condition_and_direction_short_and_trade_direction as Record<string, unknown>)
      : null;
  const by_cd_td_raw =
    regime_breakdown_raw && isPlainRecord(regime_breakdown_raw.by_candle_direction_and_trade_direction)
      ? (regime_breakdown_raw.by_candle_direction_and_trade_direction as Record<string, unknown>)
      : null;
  const by_cd_short_td_raw =
    regime_breakdown_raw &&
    isPlainRecord(regime_breakdown_raw.by_candle_direction_short_and_trade_direction)
      ? (regime_breakdown_raw.by_candle_direction_short_and_trade_direction as Record<string, unknown>)
      : null;

  const rbPick = (key: string): Record<string, ConditionAnalysisNode> =>
    regime_breakdown_raw ? parseRegimeBreakdownBranchMap((regime_breakdown_raw as PlainRecord)[key]) : {};

  const rbPickSession = (snake: string, camel: string): Record<string, ConditionAnalysisNode> => {
    if (!regime_breakdown_raw) return {};
    const pr = regime_breakdown_raw as PlainRecord;
    const raw = pr[snake] ?? pr[camel];
    return parseRegimeBreakdownBranchMap(raw);
  };

  const by_session = rbPickSession("by_session", "bySession");
  const by_session_and_trade_direction = rbPickSession("by_session_and_trade_direction", "bySessionAndTradeDirection");
  const by_session_and_candle_direction = rbPickSession("by_session_and_candle_direction", "bySessionAndCandleDirection");
  const by_session_and_candle_direction_short = rbPickSession(
    "by_session_and_candle_direction_short",
    "bySessionAndCandleDirectionShort"
  );
  const by_session_and_market_condition = rbPickSession("by_session_and_market_condition", "bySessionAndMarketCondition");
  const by_session_and_market_condition_and_trade_direction = rbPickSession(
    "by_session_and_market_condition_and_trade_direction",
    "bySessionAndMarketConditionAndTradeDirection"
  );
  const by_session_and_market_condition_and_direction_and_trade_direction = rbPickSession(
    "by_session_and_market_condition_and_direction_and_trade_direction",
    "bySessionAndMarketConditionAndDirectionAndTradeDirection"
  );
  const by_session_and_market_condition_and_direction_short_and_trade_direction = rbPickSession(
    "by_session_and_market_condition_and_direction_short_and_trade_direction",
    "bySessionAndMarketConditionAndDirectionShortAndTradeDirection"
  );

  const regime_breakdown_notes =
    regime_breakdown_raw && Array.isArray((regime_breakdown_raw as PlainRecord).notes)
      ? ((regime_breakdown_raw as PlainRecord).notes as unknown[]).filter((x): x is string => typeof x === "string")
      : undefined;

  const directional: { long?: ConditionAnalysisNode; short?: ConditionAnalysisNode } = {};
  if (dca_raw) {
    const long = parseNode(dca_raw.long);
    const short = parseNode(dca_raw.short);
    if (long) directional.long = long;
    if (short) directional.short = short;
  }

  const by_market_condition: Record<string, ConditionAnalysisNode> = {};
  if (by_mc_raw) {
    for (const [k, v] of Object.entries(by_mc_raw)) {
      const node = parseNode(v);
      if (node) by_market_condition[k] = node;
    }
  }

  const by_market_condition_and_direction_and_trade_direction: Record<string, ConditionAnalysisNode> = {};
  if (by_mcdtd_raw) {
    for (const [k, v] of Object.entries(by_mcdtd_raw)) {
      const node = parseNode(v);
      if (node) by_market_condition_and_direction_and_trade_direction[k] = node;
    }
  }

  const by_market_direction: Record<string, ConditionAnalysisNode> = {};
  if (by_mdir_raw) {
    for (const [k, v] of Object.entries(by_mdir_raw)) {
      const node = parseNode(v);
      if (node) by_market_direction[k] = node;
    }
  }

  const by_candle_direction: Record<string, ConditionAnalysisNode> = {};
  if (by_cd_raw) {
    for (const [k, v] of Object.entries(by_cd_raw)) {
      const node = parseNode(v);
      if (node) by_candle_direction[k] = node;
    }
  }

  const by_candle_direction_short: Record<string, ConditionAnalysisNode> = {};
  if (by_cd_short_raw) {
    for (const [k, v] of Object.entries(by_cd_short_raw)) {
      const node = parseNode(v);
      if (node) by_candle_direction_short[k] = node;
    }
  }

  const by_market_direction_short: Record<string, ConditionAnalysisNode> = {};
  if (by_mdir_short_raw) {
    for (const [k, v] of Object.entries(by_mdir_short_raw)) {
      const node = parseNode(v);
      if (node) by_market_direction_short[k] = node;
    }
  }

  const by_market_condition_and_direction_short_and_trade_direction: Record<string, ConditionAnalysisNode> = {};
  if (by_mcd_short_td_raw) {
    for (const [k, v] of Object.entries(by_mcd_short_td_raw)) {
      const node = parseNode(v);
      if (node) by_market_condition_and_direction_short_and_trade_direction[k] = node;
    }
  }

  const by_candle_direction_and_trade_direction: Record<string, ConditionAnalysisNode> = {};
  if (by_cd_td_raw) {
    for (const [k, v] of Object.entries(by_cd_td_raw)) {
      const node = parseNode(v);
      if (node) by_candle_direction_and_trade_direction[k] = node;
    }
  }

  const by_candle_direction_short_and_trade_direction: Record<string, ConditionAnalysisNode> = {};
  if (by_cd_short_td_raw) {
    for (const [k, v] of Object.entries(by_cd_short_td_raw)) {
      const node = parseNode(v);
      if (node) by_candle_direction_short_and_trade_direction[k] = node;
    }
  }

  return {
    version: toString(raw.version),
    analysis_scope: parseAnalysisScope(raw.analysis_scope),
    regime_summary: regime_summary_raw
      ? (() => {
          const worstShortTripleDetail = parseSummaryRows(
            regime_summary_raw.worst_market_condition_x_direction_short_x_trade_detail ??
              (regime_summary_raw as PlainRecord).worstMarketConditionXDirectionShortXTradeDetail
          );
          return {
            notes: Array.isArray(regime_summary_raw.notes)
              ? (regime_summary_raw.notes.filter((x) => typeof x === "string") as string[])
              : undefined,
            market_condition: parseSummaryRows(regime_summary_raw.market_condition),
            market_condition_and_direction: parseSummaryRows(regime_summary_raw.market_condition_and_direction),
            candle_direction: parseSummaryRows(regime_summary_raw.candle_direction),
            candle_direction_short: parseSummaryRows(regime_summary_raw.candle_direction_short),
            trade_direction: parseSummaryRows(regime_summary_raw.trade_direction),
            session: parseSummaryRows(
              regime_summary_raw.session ??
                (regime_summary_raw as PlainRecord).session_time ??
                (regime_summary_raw as PlainRecord).sessionTime
            ),
            candle_direction_and_trade_direction: parseSummaryRows(regime_summary_raw.candle_direction_and_trade_direction),
            candle_direction_short_and_trade_direction: parseSummaryRows(
              regime_summary_raw.candle_direction_short_and_trade_direction
            ),
            market_condition_candle_direction_and_trade_direction: parseSummaryRows(
              regime_summary_raw.market_condition_candle_direction_and_trade_direction
            ),
            market_condition_candle_direction_short_and_trade_direction: parseSummaryRows(
              regime_summary_raw.market_condition_candle_direction_short_and_trade_direction
            ),
            session_and_trade_direction: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_trade_direction ??
                (regime_summary_raw as PlainRecord).sessionAndTradeDirection
            ),
            session_and_candle_direction: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_candle_direction ??
                (regime_summary_raw as PlainRecord).sessionAndCandleDirection
            ),
            session_and_candle_direction_short: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_candle_direction_short ??
                (regime_summary_raw as PlainRecord).sessionAndCandleDirectionShort
            ),
            session_and_market_condition: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_market_condition ??
                (regime_summary_raw as PlainRecord).sessionAndMarketCondition
            ),
            session_and_market_condition_and_trade_direction: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_market_condition_and_trade_direction ??
                (regime_summary_raw as PlainRecord).sessionAndMarketConditionAndTradeDirection
            ),
            session_and_market_condition_and_direction_and_trade_direction: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_market_condition_and_direction_and_trade_direction ??
                (regime_summary_raw as PlainRecord).sessionAndMarketConditionAndDirectionAndTradeDirection
            ),
            session_and_market_condition_and_direction_short_and_trade_direction: parseSummaryRows(
              (regime_summary_raw as PlainRecord).session_and_market_condition_and_direction_short_and_trade_direction ??
                (regime_summary_raw as PlainRecord).sessionAndMarketConditionAndDirectionShortAndTradeDirection
            ),
            ...(worstShortTripleDetail?.length
              ? { worst_market_condition_x_direction_short_x_trade_detail: worstShortTripleDetail }
              : {}),
            uniform_segment_context: parseUniformSegmentContext(regime_summary_raw.uniform_segment_context),
            tables_omitted_for_slice_context: parseTablesOmittedForSliceContext(
              regime_summary_raw.tables_omitted_for_slice_context
            ),
          };
        })()
      : undefined,
    best_conditions: parseBucketRows(raw.best_conditions),
    worst_conditions: parseBucketRows(raw.worst_conditions),
    evaluated_buckets: toNumber(raw.evaluated_buckets),
    evaluated_situations: toNumber(raw.evaluated_situations),
    min_sample_size: toNumber(raw.min_sample_size),
    price_excursion_summary: isPlainRecord(raw.price_excursion_summary)
      ? (raw.price_excursion_summary as Record<string, unknown>)
      : undefined,
    market_condition_brackets: parseMarketConditionBrackets(raw.market_condition_brackets),
    directional_condition_analysis: Object.keys(directional).length ? directional : undefined,
    regime_breakdown:
      Object.keys(by_market_condition).length ||
      Object.keys(by_candle_direction).length ||
      Object.keys(by_candle_direction_short).length ||
      Object.keys(by_market_direction).length ||
      Object.keys(by_market_direction_short).length ||
      Object.keys(by_market_condition_and_direction_short_and_trade_direction).length ||
      Object.keys(by_market_condition_and_direction_and_trade_direction).length ||
      Object.keys(by_candle_direction_and_trade_direction).length ||
      Object.keys(by_candle_direction_short_and_trade_direction).length ||
      Object.keys(by_session).length ||
      Object.keys(by_session_and_trade_direction).length ||
      Object.keys(by_session_and_candle_direction).length ||
      Object.keys(by_session_and_candle_direction_short).length ||
      Object.keys(by_session_and_market_condition).length ||
      Object.keys(by_session_and_market_condition_and_trade_direction).length ||
      Object.keys(by_session_and_market_condition_and_direction_and_trade_direction).length ||
      Object.keys(by_session_and_market_condition_and_direction_short_and_trade_direction).length
        ? {
            ...(Object.keys(by_market_condition).length ? { by_market_condition } : {}),
            ...(Object.keys(by_candle_direction).length ? { by_candle_direction } : {}),
            ...(Object.keys(by_candle_direction_short).length ? { by_candle_direction_short } : {}),
            ...(Object.keys(by_market_direction).length ? { by_market_direction } : {}),
            ...(Object.keys(by_market_direction_short).length ? { by_market_direction_short } : {}),
            ...(Object.keys(by_market_condition_and_direction_short_and_trade_direction).length
              ? { by_market_condition_and_direction_short_and_trade_direction }
              : {}),
            ...(Object.keys(by_market_condition_and_direction_and_trade_direction).length
              ? { by_market_condition_and_direction_and_trade_direction }
              : {}),
            ...(Object.keys(by_candle_direction_and_trade_direction).length
              ? { by_candle_direction_and_trade_direction }
              : {}),
            ...(Object.keys(by_candle_direction_short_and_trade_direction).length
              ? { by_candle_direction_short_and_trade_direction }
              : {}),
            ...(Object.keys(by_session).length ? { by_session } : {}),
            ...(Object.keys(by_session_and_trade_direction).length ? { by_session_and_trade_direction } : {}),
            ...(Object.keys(by_session_and_candle_direction).length ? { by_session_and_candle_direction } : {}),
            ...(Object.keys(by_session_and_candle_direction_short).length
              ? { by_session_and_candle_direction_short }
              : {}),
            ...(Object.keys(by_session_and_market_condition).length ? { by_session_and_market_condition } : {}),
            ...(Object.keys(by_session_and_market_condition_and_trade_direction).length
              ? { by_session_and_market_condition_and_trade_direction }
              : {}),
            ...(Object.keys(by_session_and_market_condition_and_direction_and_trade_direction).length
              ? { by_session_and_market_condition_and_direction_and_trade_direction }
              : {}),
            ...(Object.keys(by_session_and_market_condition_and_direction_short_and_trade_direction).length
              ? { by_session_and_market_condition_and_direction_short_and_trade_direction }
              : {}),
          }
        : undefined,
    ...(regime_breakdown_notes?.length ? { regime_breakdown_notes } : {}),
  };
}

function resolveBySessionBreakdownKey(
  bySession: Record<string, ConditionAnalysisNode>,
  sessionRowKey: string
): string | undefined {
  const t = sessionRowKey.trim();
  if (!t) return undefined;
  if (bySession[t]) return t;
  const lower = t.toLowerCase();
  for (const k of Object.keys(bySession)) {
    if (k.trim().toLowerCase() === lower) return k;
  }
  return undefined;
}

/**
 * When `regime_summary.session` under-counts a session vs `regime_breakdown.by_session` for the same id
 * (case-insensitive keys), align the marginal row to the nested slice's `analysis_scope.trade_count` and
 * headline metrics from that node. Fixes London-only style gaps vs Session Performance / Market Context pivot.
 */
function reconcileRegimeSummarySessionWithBySession(node: ConditionAnalysisNode): ConditionAnalysisNode {
  const bySession = node.regime_breakdown?.by_session;
  const rs = node.regime_summary;
  if (!bySession || !rs?.session?.length) return node;

  let changed = false;
  const session = rs.session.map((row) => {
    const sid = row.key?.trim();
    if (!sid) return row;
    const ck = resolveBySessionBreakdownKey(bySession, sid);
    if (!ck) return row;
    const child = bySession[ck];
    const tc = child?.analysis_scope?.trade_count;
    if (typeof tc !== "number" || !Number.isFinite(tc) || tc <= 0) return row;
    const ss = row.sample_size ?? 0;
    if (Math.floor(tc) <= Math.floor(ss)) return row;
    const m = cohortMetricsFromNestedNode(child);
    changed = true;
    return {
      ...row,
      sample_size: Math.floor(tc),
      ...(typeof m.win_rate === "number" && Number.isFinite(m.win_rate) ? { win_rate: m.win_rate } : {}),
      ...(typeof m.total_pnl === "number" && Number.isFinite(m.total_pnl) ? { total_pnl: m.total_pnl } : {}),
      ...(typeof m.expectancy === "number" && Number.isFinite(m.expectancy) ? { expectancy: m.expectancy } : {}),
    };
  });

  if (!changed) return node;
  return {
    ...node,
    regime_summary: {
      ...rs,
      session,
    },
  };
}

export function getConditionAnalysisRoot(snapshot: unknown): ConditionAnalysisNode | null {
  const doc = getEvidenceDocumentRoot(snapshot);
  const root = isPlainRecord(doc) ? doc : null;
  const stats = root && isPlainRecord(root.stats) ? root.stats : null;
  const statsConditionAnalysis = stats && isPlainRecord(stats.condition_analysis) ? stats.condition_analysis : null;
  const analysisEvidence = root && isPlainRecord(root.analysis_evidence) ? root.analysis_evidence : null;
  const evidenceConditionAnalysis =
    analysisEvidence && isPlainRecord(analysisEvidence.condition_analysis) ? analysisEvidence.condition_analysis : null;
  const ca = statsConditionAnalysis ?? evidenceConditionAnalysis;
  const node = parseNode(ca);
  if (!node) return null;
  const augmented =
    stats && root ? augmentRootRegimeSummarySessionFromStats(node, root, stats) : node;
  const withPreferred = root ? preferTradeDerivedRegimeSummarySession(augmented, root) : augmented;
  return reconcileRegimeSummarySessionWithBySession(withPreferred);
}

function safeIdPart(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function candleDirectionSortKey(k: string): number {
  const x = k.toLowerCase();
  if (x === "up") return 0;
  if (x === "down") return 1;
  if (x === "flat") return 2;
  return 99;
}

function sortCandleDirectionKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const da = candleDirectionSortKey(a);
    const db = candleDirectionSortKey(b);
    if (da !== db) return da - db;
    return a.localeCompare(b);
  });
}

export function parseRegimeDirectionKey(key: string): { market_condition: string; candle_direction: string } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const [mc, dir] = parts;
  if (!mc || !dir) return null;
  return { market_condition: mc, candle_direction: dir };
}

export function parseRegimeDirectionTradeDirectionKey(
  key: string
): { market_condition: string; candle_direction: string; trade_direction: "long" | "short" } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 3) return null;
  const [mc, dir, td] = parts;
  if (!mc || !dir) return null;
  if (td !== "long" && td !== "short") return null;
  return { market_condition: mc, candle_direction: dir, trade_direction: td };
}

/**
 * Slice id used in `buildConditionAnalysisSlices` for
 * `regime_breakdown.by_market_condition_and_direction_short_and_trade_direction[segmentKey]`.
 */
export function shortTripleBreakdownSliceIdFromSegmentKey(segmentKey: string): string | null {
  const parsed = parseRegimeDirectionTradeDirectionKey(segmentKey.trim());
  if (!parsed) return null;
  return `regime_dir_short_td_${safeIdPart(parsed.market_condition)}_${safeIdPart(parsed.candle_direction)}_${parsed.trade_direction}`;
}

function parseRegimeDirectionShortTradeDirectionKey(
  key: string
): { market_condition: string; candle_direction: string; trade_direction: "long" | "short" } | null {
  // Same delimiter format; candle_direction is the short-window key (up/down/flat).
  return parseRegimeDirectionTradeDirectionKey(key);
}

/** Keys like down|long — long-window candle direction × trade side. */
export function parseCandleDirectionTradeDirectionKey(
  key: string
): { candle_direction: string; trade_direction: "long" | "short" } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const [cd, td] = parts;
  if (!cd || !td) return null;
  if (td !== "long" && td !== "short") return null;
  return { candle_direction: cd, trade_direction: td };
}

/** `by_session_and_trade_direction`: `{session}|{long|short}` */
export function parseSessionTradeDirectionKey(
  key: string
): { session_id: string; trade_direction: "long" | "short" } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const [sid, td] = parts;
  if (!sid || (td !== "long" && td !== "short")) return null;
  return { session_id: sid, trade_direction: td };
}

/** `by_session_and_candle_direction` / `_short`: `{session}|{up|down|flat}` */
export function parseSessionCandleDirectionKey(
  key: string
): { session_id: string; candle_direction: string } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const [sid, cd] = parts;
  if (!sid || !cd) return null;
  const cdl = cd.toLowerCase();
  if (cdl !== "up" && cdl !== "down" && cdl !== "flat") return null;
  return { session_id: sid, candle_direction: cdl };
}

/** `by_session_and_market_condition`: `{session}|{condition}` — rejects `|long` / `|short` duplet. */
export function parseSessionMarketConditionKey(
  key: string
): { session_id: string; market_condition: string } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const [sid, mc] = parts;
  if (!sid || !mc) return null;
  const ml = mc.toLowerCase();
  if (ml === "long" || ml === "short") return null;
  return { session_id: sid, market_condition: mc };
}

/** `by_session_and_market_condition_and_trade_direction`: `{session}|{condition}|{long|short}` */
export function parseSessionMarketConditionTradeDirectionKey(
  key: string
): { session_id: string; market_condition: string; trade_direction: "long" | "short" } | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 3) return null;
  const [sid, mc, td] = parts;
  if (!sid || !mc || (td !== "long" && td !== "short")) return null;
  return { session_id: sid, market_condition: mc, trade_direction: td };
}

/** Long-window quad under session-first maps. */
export function parseSessionMarketConditionCandleTradeDirectionKey(
  key: string
): {
  session_id: string;
  market_condition: string;
  candle_direction: string;
  trade_direction: "long" | "short";
} | null {
  const parts = key.split("|").map((p) => p.trim());
  if (parts.length !== 4) return null;
  const [sid, mc, cd, td] = parts;
  if (!sid || !mc || !cd || (td !== "long" && td !== "short")) return null;
  const cdl = cd.toLowerCase();
  if (cdl !== "up" && cdl !== "down" && cdl !== "flat") return null;
  return { session_id: sid, market_condition: mc, candle_direction: cdl, trade_direction: td };
}

export function buildConditionAnalysisSlices(
  snapshot: unknown,
  candleTimeframe?: string | null
): ConditionAnalysisSlice[] {
  const root = getConditionAnalysisRoot(snapshot);
  if (!root) return [];

  const longWin = formatDirectionBarsWindow("long", candleTimeframe);
  const shortWin = formatDirectionBarsWindow("short", candleTimeframe);

  const slices: ConditionAnalysisSlice[] = [];
  slices.push({ id: "all", label: "All trades", kind: "all", node: root });

  const dca = root.directional_condition_analysis;
  if (dca?.long) slices.push({ id: "dir_long", label: "Long", kind: "direction", direction: "long", node: dca.long });
  if (dca?.short) slices.push({ id: "dir_short", label: "Short", kind: "direction", direction: "short", node: dca.short });

  const byCd = root.regime_breakdown?.by_candle_direction;
  const byCdShort = root.regime_breakdown?.by_candle_direction_short;

  const byMc = root.regime_breakdown?.by_market_condition;
  if (byMc) {
    const keys = Object.keys(byMc).sort((a, b) => a.localeCompare(b));
    for (const mc of keys) {
      const node = byMc[mc];
      slices.push({
        id: `regime_${safeIdPart(mc)}`,
        label: `Regime: ${mc}`,
        kind: "regime",
        market_condition: mc,
        node,
      });
    }
  }

  const byMdir = root.regime_breakdown?.by_market_direction;
  if (byMdir) {
    const keys = sortCandleDirectionKeys(Object.keys(byMdir));
    for (const dir of keys) {
      const node = byMdir[dir];
      slices.push({
        id: `mkt_dir_${safeIdPart(dir)}`,
        label: `Market candle direction (${longWin}): ${dir}`,
        kind: "market_direction",
        candle_direction: dir,
        node,
      });
    }
  }

  const byMdirShort = root.regime_breakdown?.by_market_direction_short;
  if (byMdirShort) {
    const keys = sortCandleDirectionKeys(Object.keys(byMdirShort));
    for (const dir of keys) {
      const node = byMdirShort[dir];
      slices.push({
        id: `mkt_dir_short_${safeIdPart(dir)}`,
        label: `Market candle direction (${shortWin}): ${dir}`,
        kind: "market_direction_short",
        candle_direction: dir,
        node,
      });
    }
  }

  /**
   * Backend provides BOTH:
   * - `regime_summary.candle_direction` (marginal headline table)
   * - `regime_breakdown.by_market_direction` (nested drilldown map for up/down)
   * - `regime_breakdown.by_candle_direction` (nested drilldown map for up/down/flat)
   *
   * To avoid duplicating up/down in the UI, only add candle-direction drill slices for directions
   * not already covered by the market-direction drill map (i.e. typically `flat`).
   */
  if (byCd) {
    const covered = new Set(Object.keys(byMdir ?? {}));
    for (const dir of sortCandleDirectionKeys(Object.keys(byCd))) {
      if (covered.has(dir)) continue;
      slices.push({
        id: `candle_dir_${safeIdPart(dir)}`,
        label: `Market candle direction (${longWin}): ${dir}`,
        kind: "candle_direction",
        candle_direction: dir,
        node: byCd[dir],
      });
    }
  }

  if (byCdShort) {
    const covered = new Set(Object.keys(byMdirShort ?? {}));
    for (const dir of sortCandleDirectionKeys(Object.keys(byCdShort))) {
      if (covered.has(dir)) continue;
      slices.push({
        id: `mkt_dir_short_${safeIdPart(dir)}`,
        label: `Market candle direction (${shortWin}): ${dir}`,
        kind: "market_direction_short",
        candle_direction: dir,
        node: byCdShort[dir],
      });
    }
  }

  // New nested map: real fully-computed nodes per (market_condition|candle_direction|trade_direction).
  const byMcdTd = root.regime_breakdown?.by_market_condition_and_direction_and_trade_direction;
  if (byMcdTd) {
    const keys = Object.keys(byMcdTd).sort((a, b) => a.localeCompare(b));
    for (const k of keys) {
      const parsed = parseRegimeDirectionTradeDirectionKey(k);
      const node = byMcdTd[k];
      if (!parsed) continue;
      slices.push({
        id: `regime_dir_td_${safeIdPart(parsed.market_condition)}_${safeIdPart(parsed.candle_direction)}_${parsed.trade_direction}`,
        label: `${parsed.market_condition} | ${parsed.candle_direction} | ${parsed.trade_direction} (${longWin})`,
        kind: "regime_direction_trade_direction",
        market_condition: parsed.market_condition,
        candle_direction: parsed.candle_direction,
        direction: parsed.trade_direction,
        node,
      });
    }
  }

  const byMcDirShortTd =
    root.regime_breakdown?.by_market_condition_and_direction_short_and_trade_direction;
  if (byMcDirShortTd) {
    const keys = Object.keys(byMcDirShortTd).sort((a, b) => a.localeCompare(b));
    for (const k of keys) {
      const parsed = parseRegimeDirectionShortTradeDirectionKey(k);
      const node = byMcDirShortTd[k];
      if (!parsed) continue;
      slices.push({
        id: `regime_dir_short_td_${safeIdPart(parsed.market_condition)}_${safeIdPart(parsed.candle_direction)}_${parsed.trade_direction}`,
        label: `${parsed.market_condition} | ${parsed.candle_direction} | ${parsed.trade_direction} (${shortWin})`,
        kind: "regime_direction_trade_direction",
        market_condition: parsed.market_condition,
        candle_direction: parsed.candle_direction,
        direction: parsed.trade_direction,
        node,
      });
    }
  }

  const byCdTd = root.regime_breakdown?.by_candle_direction_and_trade_direction;
  if (byCdTd) {
    const keys = Object.keys(byCdTd).sort((a, b) => a.localeCompare(b));
    for (const k of keys) {
      const parsed = parseCandleDirectionTradeDirectionKey(k);
      const node = byCdTd[k];
      if (!parsed) continue;
      slices.push({
        id: `cd_td_${safeIdPart(parsed.candle_direction)}_${parsed.trade_direction}`,
        label: `${parsed.candle_direction} | ${parsed.trade_direction} · Market candle direction (${longWin}) + trade direction`,
        kind: "candle_direction_trade_direction",
        candle_direction: parsed.candle_direction,
        direction: parsed.trade_direction,
        node,
      });
    }
  }

  const byCdShortTd = root.regime_breakdown?.by_candle_direction_short_and_trade_direction;
  if (byCdShortTd) {
    const keys = Object.keys(byCdShortTd).sort((a, b) => a.localeCompare(b));
    for (const k of keys) {
      const parsed = parseCandleDirectionTradeDirectionKey(k);
      const node = byCdShortTd[k];
      if (!parsed) continue;
      slices.push({
        id: `cd_short_td_${safeIdPart(parsed.candle_direction)}_${parsed.trade_direction}`,
        label: `${parsed.candle_direction} | ${parsed.trade_direction} · Market candle direction (${shortWin}) + trade direction`,
        kind: "candle_direction_short_trade_direction",
        candle_direction: parsed.candle_direction,
        direction: parsed.trade_direction,
        node,
      });
    }
  }

  // Derived “virtual” slices from the root market_condition_and_direction table (summary-only POV).
  const mcd = root.regime_summary?.market_condition_and_direction ?? [];
  for (const row of mcd) {
    const parsed = parseRegimeDirectionKey(row.key);
    if (!parsed) continue;
    slices.push({
      id: `regime_dir_${safeIdPart(parsed.market_condition)}_${safeIdPart(parsed.candle_direction)}`,
      label: `${parsed.market_condition} | ${parsed.candle_direction}`,
      kind: "regime_direction",
      market_condition: parsed.market_condition,
      candle_direction: parsed.candle_direction,
      node: root,
    });
  }

  const bySession = root.regime_breakdown?.by_session;
  if (bySession && Object.keys(bySession).length > 0) {
    const keys = Object.keys(bySession).sort((a, b) => a.localeCompare(b));
    for (const S of keys) {
      slices.push({
        id: `session_${safeIdPart(S)}`,
        label: `Session: ${S.replace(/_/g, " ")}`,
        kind: "session_time",
        session_id: S,
        node: bySession[S],
      });
    }
  } else {
    const sessionRows = root.regime_summary?.session ?? [];
    for (const row of sessionRows) {
      const sid = String(row.key ?? "").trim();
      if (!sid) continue;
      slices.push({
        id: `session_${safeIdPart(sid)}`,
        label: `Session: ${sid.replace(/_/g, " ")}`,
        kind: "session_time",
        session_id: sid,
        node: root,
      });
    }
  }

  const bySessTd = root.regime_breakdown?.by_session_and_trade_direction;
  if (bySessTd) {
    for (const k of Object.keys(bySessTd).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionTradeDirectionKey(k);
      const node = bySessTd[k];
      if (!p) continue;
      slices.push({
        id: `sess_td_${safeIdPart(p.session_id)}_${p.trade_direction}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.trade_direction}`,
        kind: "session_trade_direction",
        session_id: p.session_id,
        direction: p.trade_direction,
        node,
      });
    }
  }

  const bySessCd = root.regime_breakdown?.by_session_and_candle_direction;
  if (bySessCd) {
    for (const k of Object.keys(bySessCd).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionCandleDirectionKey(k);
      const node = bySessCd[k];
      if (!p) continue;
      slices.push({
        id: `sess_cd_${safeIdPart(p.session_id)}_${p.candle_direction}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.candle_direction} · Market candle direction (${longWin})`,
        kind: "session_candle_direction",
        session_id: p.session_id,
        candle_direction: p.candle_direction as CandleDirectionKey,
        node,
      });
    }
  }

  const bySessCdShort = root.regime_breakdown?.by_session_and_candle_direction_short;
  if (bySessCdShort) {
    for (const k of Object.keys(bySessCdShort).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionCandleDirectionKey(k);
      const node = bySessCdShort[k];
      if (!p) continue;
      slices.push({
        id: `sess_cds_${safeIdPart(p.session_id)}_${p.candle_direction}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.candle_direction} · Market candle direction (${shortWin})`,
        kind: "session_candle_direction_short",
        session_id: p.session_id,
        candle_direction: p.candle_direction as CandleDirectionKey,
        node,
      });
    }
  }

  const bySessMc = root.regime_breakdown?.by_session_and_market_condition;
  if (bySessMc) {
    for (const k of Object.keys(bySessMc).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionMarketConditionKey(k);
      const node = bySessMc[k];
      if (!p) continue;
      slices.push({
        id: `sess_mc_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition}`,
        kind: "session_regime",
        session_id: p.session_id,
        market_condition: p.market_condition as ConditionAnalysisRegimeKey,
        node,
      });
    }
  }

  const bySessMcTd = root.regime_breakdown?.by_session_and_market_condition_and_trade_direction;
  if (bySessMcTd) {
    for (const k of Object.keys(bySessMcTd).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionMarketConditionTradeDirectionKey(k);
      const node = bySessMcTd[k];
      if (!p) continue;
      slices.push({
        id: `sess_mc_td_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}_${p.trade_direction}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition} | ${p.trade_direction}`,
        kind: "session_regime_trade_direction",
        session_id: p.session_id,
        market_condition: p.market_condition as ConditionAnalysisRegimeKey,
        direction: p.trade_direction,
        node,
      });
    }
  }

  const bySessMcdTd = root.regime_breakdown?.by_session_and_market_condition_and_direction_and_trade_direction;
  if (bySessMcdTd) {
    for (const k of Object.keys(bySessMcdTd).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionMarketConditionCandleTradeDirectionKey(k);
      const node = bySessMcdTd[k];
      if (!p) continue;
      slices.push({
        id: `sess_mcd_td_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}_${p.candle_direction}_${p.trade_direction}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition} | ${p.candle_direction} | ${p.trade_direction} (${longWin})`,
        kind: "session_regime_direction_trade_direction",
        session_id: p.session_id,
        market_condition: p.market_condition as ConditionAnalysisRegimeKey,
        candle_direction: p.candle_direction as CandleDirectionKey,
        direction: p.trade_direction,
        node,
      });
    }
  }

  const bySessMcdShortTd =
    root.regime_breakdown?.by_session_and_market_condition_and_direction_short_and_trade_direction;
  if (bySessMcdShortTd) {
    for (const k of Object.keys(bySessMcdShortTd).sort((a, b) => a.localeCompare(b))) {
      const p = parseSessionMarketConditionCandleTradeDirectionKey(k);
      const node = bySessMcdShortTd[k];
      if (!p) continue;
      slices.push({
        id: `sess_mcdstd_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}_${p.candle_direction}_${p.trade_direction}`,
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition} | ${p.candle_direction} | ${p.trade_direction} (${shortWin})`,
        kind: "session_regime_direction_short_trade_direction",
        session_id: p.session_id,
        market_condition: p.market_condition as ConditionAnalysisRegimeKey,
        candle_direction: p.candle_direction as CandleDirectionKey,
        direction: p.trade_direction,
        node,
      });
    }
  }

  backfillPivotDirectionSlicesFromSummary(slices, root, candleTimeframe);

  return slices;
}

function backfillPivotDirectionSlicesFromSummary(
  slices: ConditionAnalysisSlice[],
  root: ConditionAnalysisNode,
  candleTimeframe?: string | null
): void {
  const existing = new Set(
    slices
      .filter((s) => s.candle_direction)
      .map((s) => {
        const w = s.kind === "market_direction_short" || s.kind === "candle_direction_short" ? "short" : "long";
        return `${w}:${String(s.candle_direction).toLowerCase()}`;
      })
  );

  const add = (
    horizon: "long" | "short",
    rows: ConditionAnalysisSummaryRow[] | undefined,
    breakdown: Record<string, ConditionAnalysisNode> | undefined,
    kind: "market_direction" | "market_direction_short"
  ) => {
    if (!rows?.length || !breakdown) return;
    const bars = formatDirectionBarsWindow(horizon, candleTimeframe);
    for (const row of rows) {
      const dir = String(row.key ?? "").trim().toLowerCase();
      if (!dir || dir === "unknown" || dir === "rollup_below_min_sample") continue;
      const dedupe = `${horizon}:${dir}`;
      if (existing.has(dedupe)) continue;
      const node = breakdown[dir] ?? breakdown[String(row.key ?? "")];
      if (!node) continue;
      existing.add(dedupe);
      slices.push({
        id: horizon === "short" ? `mkt_dir_short_${safeIdPart(dir)}` : `mkt_dir_${safeIdPart(dir)}`,
        label: `Market candle direction (${bars}): ${dir}`,
        kind,
        candle_direction: dir,
        node,
      });
    }
  };

  const rb = root.regime_breakdown;
  const rs = root.regime_summary;
  add("long", rs?.candle_direction, rb?.by_candle_direction, "market_direction");
  add("short", rs?.candle_direction_short, rb?.by_candle_direction_short, "market_direction_short");
}

export function bucketMetricKeyFromBucket(bucket: string): string {
  const idx = bucket.indexOf(":");
  const raw = idx <= 0 ? bucket : bucket.slice(0, idx);
  const metric = String(raw || "").trim().toLowerCase();
  // Bucket prefixes are short-friendly labels; map to canonical metric-definition keys.
  if (metric === "atr") return "atr_regime";
  return raw;
}

